/* eslint-disable camelcase */

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;

// Use window.posthog initialized by the HTML snippet in index.html.
// The posthog-js npm module creates a separate uninitialized singleton
// that doesn't share state with window.posthog, so we proxy to the global.
const noop = () => {};
const posthog = POSTHOG_KEY
  ? new Proxy(
      {},
      {
        get: (_target, prop) => {
          const ph = typeof window !== 'undefined' && window.posthog;
          if (ph && typeof ph[prop] === 'function') {
            return ph[prop].bind(ph);
          }
          return ph ? ph[prop] : noop;
        },
      },
    )
  : new Proxy(
      {},
      {
        get: () => noop,
      },
    );

/**
 * Identifies and sets user properties for PostHog tracking
 * @param {Object} options - User properties
 * @param {Object} options.agency - Agency object with id, agencyCode, name
 * @param {Object} options.agent - Agent object with id, email, fullName
 * @param {Object} options.client - Client object with id, email, name/fullName, dealId, dealIdSource
 * @param {string} options.userType - Type of user ('agent' or 'client')
 */
export const identifyPostHogUser = ({ agency, agent, client, userType }) => {
  const distinctId = userType === 'agent' ? agent?.email : (client?.email ?? (client?.id ? `client-${client.id}` : null));

  if (!distinctId) {
    return;
  }

  // Agent app uses agency.name, client app uses agency.agencyName
  const agencyName = agency?.name || agency?.agencyName;

  const userProperties = {};

  // Agency properties
  if (agency?.id) userProperties.agency_id = agency.id;
  if (agency?.agencyCode) userProperties.agency_code = agency.agencyCode;
  if (agencyName) userProperties.agency_name = agencyName;

  // Agent properties
  if (agent?.id) userProperties.agent_id = agent.id;
  if (agent?.email) userProperties.agent_email = agent.email;
  if (agent?.fullName) userProperties.agent_name = agent.fullName;

  // Client properties
  if (client?.id) userProperties.client_id = client.id;
  if (client?.email) userProperties.client_email = client.email;
  if (client?.name || client?.fullName) userProperties.client_name = client.name || client.fullName;
  if (client?.dealId) userProperties.deal_id = client.dealId;
  if (client?.dealIdSource) userProperties.deal_id_source = client.dealIdSource;

  // PostHog displays persons using top-level `email` and `name` properties
  const email = userType === 'agent' ? agent?.email : client?.email;
  const name = userType === 'agent' ? agent?.fullName : client?.name || client?.fullName;
  if (email) userProperties.email = email;
  if (name) userProperties.name = name;

  // User type
  if (userType) userProperties.user_type = userType;

  posthog.identify(distinctId, userProperties);

  // Super properties — attached to every event for easy filtering/breakdown
  const superProps = {};
  if (userType) superProps.user_type = userType;
  if (agency?.agencyCode) superProps.agency_code = agency.agencyCode;
  if (agencyName) superProps.agency_name = agencyName;
  posthog.register(superProps);

  // Group analytics — agency-level tracking (keyed on agency.id, unique per tenant)
  if (agency?.id) {
    const groupProperties = {};
    if (agencyName) groupProperties.name = agencyName;
    if (agency?.agencyCode) groupProperties.agency_code = agency.agencyCode;
    posthog.group('agency', agency.id, groupProperties);
  }
};

/**
 * Resets the PostHog user (call on logout)
 */
export const resetPostHogUser = () => {
  posthog.reset();
};
